import React, { useState } from 'react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { useTranslation } from '../../../lib/i18n';
import { autoPostAPI } from '../../../lib/api';
import { toast } from 'sonner';
import {
  Zap, Clock, CheckCircle, User, Building2, Star, Instagram,
  Loader2, Sparkles, PartyPopper
} from 'lucide-react';

export default function ActivateStep({ config, onConfigChange, wizardData, onComplete }) {
  const { t } = useTranslation();
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);

  const handleActivate = async () => {
    setActivating(true);
    try {
      await autoPostAPI.updateConfig({
        is_enabled: true,
        posts_per_day: config.posts_per_day,
        preferred_time: config.preferred_time,
        timezone: config.timezone,
        language: wizardData.contentLanguage || 'de',
        // Liked templates from the sampler swipe step (new onboarding v2).
        // Fall back to the legacy `favoriteTemplates` key for back-compat
        // during the transition in case any state still lives under it.
        favorite_templates:
          wizardData.likedTemplates ?? wizardData.favoriteTemplates ?? [],
        platforms: wizardData.connectedSocials?.map((s) => s.platform) || ['instagram', 'facebook'],
        auto_select_template: true,
        auto_generate_caption: true,
        auto_generate_hashtags: true,
        enable_voiceover: wizardData.enableVoiceover !== false,
        // Voice picker output. Either a curated key ("matilda") or a raw
        // voice_id from the advanced field. Null = template default.
        voice_id: wizardData.voiceId || null,
        // Multi-select music: liked preset ids array + optional upload URL.
        // Scheduler chooses one per auto-post at random.
        background_music_preset_ids: wizardData.selectedMusicPresetIds || [],
        background_music_url: wizardData.selectedMusicUploadUrl || null,
      });
      setActivated(true);
      toast.success(t('onboarding.step7.activated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('onboarding.step7.activationFailed'));
    } finally {
      setActivating(false);
    }
  };

  if (activated) {
    return (
      <div className="text-center py-8 space-y-6">
        <div className="inline-flex p-6 bg-[#d6e8dd] rounded-3xl">
          <PartyPopper className="w-16 h-16 text-[#1e7a4f]" />
        </div>
        <div>
          <h2 className="text-3xl font-bold text-[#23211a] mb-3">
            {t('onboarding.step7.congratulations')}
          </h2>
          <p className="text-[#6f6a5c] max-w-md mx-auto">
            {t('onboarding.step7.congDesc')}
          </p>
        </div>
        <Button
          onClick={onComplete}
          className="bg-[#1f4d3a] hover:bg-[#163a2c] px-8 text-lg py-3"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          {t('onboarding.step7.goToDashboard')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex p-4 bg-[#d6e8dd] rounded-2xl mb-4">
          <Zap className="w-10 h-10 text-[#1e7a4f]" />
        </div>
        <h2 className="text-2xl font-bold text-[#23211a] mb-2">
          {t('onboarding.step7.title')}
        </h2>
        <p className="text-[#6f6a5c]">
          {t('onboarding.step7.subtitle')}
        </p>
      </div>

      {/* Summary */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-[#4a463b] mb-3">
          {t('onboarding.step7.summary')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="flex items-center gap-2 p-3 bg-[#fffdf7] rounded-lg border border-[#e3dccb]">
            <CheckCircle className="w-4 h-4 text-[#1e7a4f] shrink-0" />
            <span className="text-sm text-[#4a463b]">
              {t('onboarding.step7.summaryProfile')}
            </span>
          </div>
          <div className="flex items-center gap-2 p-3 bg-[#fffdf7] rounded-lg border border-[#e3dccb]">
            <Building2 className="w-4 h-4 text-[#8f6f3a] shrink-0" />
            <span className="text-sm text-[#4a463b]">
              {t('onboarding.step7.summaryProperties', {
                count: wizardData.propertiesImported || 0,
              })}
            </span>
          </div>
          <div className="flex items-center gap-2 p-3 bg-[#fffdf7] rounded-lg border border-[#e3dccb]">
            <Star className="w-4 h-4 text-[#9a6d1f] shrink-0" />
            <span className="text-sm text-[#4a463b]">
              {t('onboarding.step7.summaryTemplates', {
                count:
                  wizardData.likedTemplates?.length ??
                  wizardData.favoriteTemplates?.length ??
                  0,
              })}
            </span>
          </div>
          <div className="flex items-center gap-2 p-3 bg-[#fffdf7] rounded-lg border border-[#e3dccb]">
            <Instagram className="w-4 h-4 text-pink-400 shrink-0" />
            <span className="text-sm text-[#4a463b]">
              {t('onboarding.step7.summarySocials', {
                count: wizardData.connectedSocials?.length || 0,
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Schedule Configuration */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-[#4a463b]">{t('autoPost.schedule')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-[#6f6a5c] mb-1.5 block">
              {t('onboarding.step7.postsPerDay')}
            </label>
            <select
              value={config.posts_per_day}
              onChange={(e) =>
                onConfigChange({ ...config, posts_per_day: parseInt(e.target.value) })
              }
              className="w-full h-10 rounded-md bg-[#fffdf7] border border-[#d8d0bd] text-[#23211a] px-3"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-[#6f6a5c] mb-1.5 block">
              {t('onboarding.step7.preferredTime')}
            </label>
            <Input
              type="time"
              value={config.preferred_time}
              onChange={(e) =>
                onConfigChange({ ...config, preferred_time: e.target.value })
              }
              className="bg-[#fffdf7] border-[#d8d0bd]"
            />
          </div>
          <div>
            <label className="text-xs text-[#6f6a5c] mb-1.5 block">
              {t('onboarding.step7.timezone')}
            </label>
            <select
              value={config.timezone}
              onChange={(e) =>
                onConfigChange({ ...config, timezone: e.target.value })
              }
              className="w-full h-10 rounded-md bg-[#fffdf7] border border-[#d8d0bd] text-[#23211a] px-3"
            >
              <option value="Europe/Vienna">{t('timezones.vienna')}</option>
              <option value="Europe/Berlin">{t('timezones.berlin')}</option>
              <option value="Europe/Zurich">{t('timezones.zurich')}</option>
              <option value="Europe/London">{t('timezones.london')}</option>
              <option value="America/New_York">{t('timezones.newYork')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Activate Buttons */}
      <div className="flex flex-col items-center gap-3 pt-4">
        <Button
          onClick={handleActivate}
          disabled={activating || wizardData.connectedSocials?.length === 0}
          className="bg-[#1e7a4f] hover:bg-[#155c3b] text-white px-10 py-3 text-base"
        >
          {activating ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              {t('common.loading')}
            </>
          ) : (
            <>
              <Zap className="w-5 h-5 mr-2" />
              {t('onboarding.step7.activateAutoPost')}
            </>
          )}
        </Button>

        {wizardData.connectedSocials?.length === 0 && (
          <p className="text-xs text-[#9a6d1f]">
            {t('autoPost.enableWithoutSocials')}
          </p>
        )}

        <Button
          variant="ghost"
          onClick={onComplete}
          className="text-[#8a8474] hover:text-[#4a463b] text-sm"
        >
          {t('onboarding.step7.skipActivation')}
        </Button>
      </div>
    </div>
  );
}
